/* eslint no-param-reassign: 0 */
import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import { frontendURL } from '../../helper/URLHelper';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../helper/scriptHelpers';

Cookies.defaults = { sameSite: 'Lax' };

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

const sanitizeAccounts = accounts => {
  if (!Array.isArray(accounts)) return [];
  // Keep only minimal fields to stay well under browser cookie limits (~4 KB)
  // and drop duplicates.
  const seen = new Set();
  const trimmed = [];
  for (const acc of accounts) {
    if (!acc || !acc.id) continue;
    if (seen.has(acc.id)) continue;
    seen.add(acc.id);
    const { id, name, role, availability, availability_status, auto_offline, stripe, stripe_account } = acc;
    trimmed.push({ id, name, role, availability: availability || availability_status, auto_offline, stripe, stripe_account });
    if (trimmed.length >= 200) break; // hard cap to protect cookie size
  }
  return trimmed;
};

export const setUser = (user, expiryDate, options = {}) => {
  if (options && options.setUserInSDK) {
    window.bus.$emit(CHATWOOT_SET_USER, { user });
    window.bus.$emit(ANALYTICS_IDENTITY, { user });
  }

  // Persist only the minimal fields needed for routing; large payloads can
  // exceed 4KB cookie limits and silently fail to set (seen in prod).
  const safeUser = typeof user === 'object' && user !== null ? user : {};
  const primaryAccount = Array.isArray(safeUser.accounts)
    ? safeUser.accounts.find(acc => acc && acc.id)
    : null;

  const userCookie = {
    id: safeUser.id,
    email: safeUser.email,
    name: safeUser.name,
    uid: safeUser.uid,
    account_id: safeUser.account_id || primaryAccount?.id,
    role: safeUser.role || primaryAccount?.role,
    pubsub_token: safeUser.pubsub_token,
  };

  // Guardrail: if the serialized cookie would exceed ~3KB, fall back to a
  // tiny payload to guarantee the cookie is accepted by the browser.
  const serialized = JSON.stringify(userCookie);
  if (serialized.length > 3000) {
    Cookies.set(
      'user',
      {
        id: userCookie.id,
        uid: userCookie.uid,
        account_id: userCookie.account_id,
        role: userCookie.role,
      },
      {
        expires: differenceInDays(expiryDate, new Date()),
      }
    );
    return;
  }

  Cookies.set('user', userCookie, {
    expires: differenceInDays(expiryDate, new Date()),
  });
};

export const getHeaderExpiry = response =>
  fromUnixTime(response.headers.expiry);

export const setAuthCredentials = response => {
  if (!response || !response.headers) return;
  const expiryDate = getHeaderExpiry(response);
  const authHeaders = {
    'access-token': response.headers['access-token'],
    client: response.headers.client,
    uid: response.headers.uid,
    expiry: response.headers.expiry,
    'token-type': response.headers['token-type'],
  };

  Cookies.set('auth_data', JSON.stringify(authHeaders), {
    expires: differenceInDays(expiryDate, new Date()),
  });

  const payload = response?.data?.data;
  // Fallback to minimal user built from headers if body is missing or non-object
  const userPayload =
    payload && typeof payload === 'object'
      ? payload
      : {
          uid: response.headers.uid,
          email: response.headers.uid,
          account_id: response.headers['account-id'],
        };

  setUser(userPayload, expiryDate);
};

export const clearCookiesOnLogout = () => {
  window.bus.$emit(CHATWOOT_RESET);
  window.bus.$emit(ANALYTICS_RESET);
  Cookies.remove('auth_data');
  Cookies.remove('user');
  window.location = frontendURL('login');
};
